// Leak detection methods, en anglais britannique. translationOf : page
// française équivalente.
import pressionImg from '../assets/controle_pression.webp';
import acoustiqueImg from '../assets/ecoute_acoustique.webp';
import fluoresceineImg from '../assets/fluoresceine_uv.webp';
import gazImg from '../assets/gaz_traceur_detecteur.webp';
import thermoImg from '../assets/thermographie_chauffage_sol.webp';
import humidimetreImg from '../assets/humidimetre_mur.webp';
import inspectionImg from '../assets/inspection_camera_egout.webp';

const PARENT = '/en/leak-detection-methods';

const technique = (t) => ({ lang: 'en', parent: PARENT, eyebrow: 'Leak detection methods', ...t });

export const techniquesEn = [
  {
    path: '/en/leak-detection-methods',
    lang: 'en',
    translationOf: '/techniques',
    parent: '/en/leak-detection-luxembourg',
    nav: 'Our methods',
    summary: 'Nine non-destructive methods, chosen and combined according to the type of leak.',
    title: 'Non-destructive leak detection methods | Expomis',
    description: 'Thermal imaging, acoustic detection, tracer gas, fluorescein, smoke test, CCTV drain survey, endoscopy, pressure testing, moisture mapping: which method when.',
    eyebrow: 'Leak detection methods',
    h1: 'Our leak detection methods',
    intro: [
      'No single method finds every leak. A thermal camera cannot see a cold water leak, acoustic detection hears nothing on a drain, and tracer gas needs a pipe that can be isolated. Expomis uses nine non-destructive methods and combines them to cross-check the results before marking the position of a leak.',
    ],
    sections: [
      {
        h2: 'Which method for which leak?',
        list: [
          'Every job: [moisture mapping](/en/leak-detection-methods/moisture-mapping) to define the affected area, and a [pressure test](/en/leak-detection-methods/pressure-test) to confirm the leak and identify the circuit.',
          'Hot water, underfloor heating: [thermal imaging](/en/leak-detection-methods/thermal-imaging), then [tracer gas](/en/leak-detection-methods/tracer-gas) if the contrast is not strong enough.',
          'Pressurised cold water, buried pipe: [acoustic detection](/en/leak-detection-methods/acoustic-detection) and tracer gas.',
          'Drains and sewers: [fluorescein](/en/leak-detection-methods/fluorescein), [smoke test](/en/leak-detection-methods/smoke-test), [CCTV drain survey](/en/leak-detection-methods/cctv-drain-survey).',
          'Water ingress: moisture mapping, fluorescein, thermal imaging.',
          'Hidden spaces (suspended ceiling, service duct, dry lining): [endoscopy](/en/leak-detection-methods/endoscopy).',
        ],
      },
    ],
    childrenTitle: 'The nine methods in detail',
    children: [
      '/en/leak-detection-methods/thermal-imaging',
      '/en/leak-detection-methods/acoustic-detection',
      '/en/leak-detection-methods/tracer-gas',
      '/en/leak-detection-methods/fluorescein',
      '/en/leak-detection-methods/pressure-test',
      '/en/leak-detection-methods/moisture-mapping',
      '/en/leak-detection-methods/smoke-test',
      '/en/leak-detection-methods/cctv-drain-survey',
      '/en/leak-detection-methods/endoscopy',
    ],
  },
  technique({
    path: '/en/leak-detection-methods/thermal-imaging',
    translationOf: '/techniques/camera-thermique',
    nav: 'Thermal imaging',
    summary: 'Shows the heat from a hot water or underfloor heating leak, without contact.',
    serviceType: 'Leak detection by thermal imaging',
    title: 'Leak detection with a thermal imaging camera | Expomis',
    description: 'How a thermal camera locates a leak on a hot water pipe or underfloor heating, when it works and what its limits are.',
    h1: 'Leak detection with a thermal imaging camera',
    image: thermoImg,
    imageAlt: 'Thermal image showing underfloor heating pipes and the warm patch of a leak',
    intro: [
      'Infrared thermography measures surface temperatures from a distance and turns them into an image. When hot water escapes from a pipe or an underfloor heating loop, it warms the screed or the wall around the leak: the thermal camera reveals this warm patch without touching anything.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'The camera does not see through walls: it reads the surface temperature, and what is happening behind it is deduced from the temperature differences. For a hot water or heating leak, the circuit is heated up and we watch the heat spread: first the layout of the pipes appears, then the area where the water is spreading.',
          'On a wet wall it works the other way round: evaporation cools the surface, so the damp area shows up colder than the rest.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          '[underfloor heating](/en/heating-leak) and heating pipes;',
          'concealed hot water pipes;',
          'mapping the extent of a damp area after [water ingress](/en/water-ingress) or water damage.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'It needs a temperature difference. A cold water leak in a wall at room temperature is hard to see. A thick floor covering or floorboards laid on battens weaken the signal, and shiny surfaces reflect the radiation. In those cases we switch to [tracer gas](/en/leak-detection-methods/tracer-gas) or [acoustic detection](/en/leak-detection-methods/acoustic-detection).',
        ],
      },
    ],
    faq: [
      {
        q: 'Can a thermal camera see through walls?',
        a: 'No. It measures the surface temperature. It is the escaping hot water that changes this temperature and gives away the position of the leak.',
      },
    ],
    related: ['/en/heating-leak', '/en/leak-detection-methods/tracer-gas', '/en/leak-detection-methods/moisture-mapping'],
  }),
  technique({
    path: '/en/leak-detection-methods/acoustic-detection',
    translationOf: '/techniques/detection-acoustique',
    nav: 'Acoustic detection',
    summary: 'Electro-acoustic listening and correlation: the sound of water escaping under pressure.',
    serviceType: 'Acoustic leak detection',
    title: 'Acoustic leak detection | Expomis',
    description: 'Electro-acoustic listening and acoustic correlation: how a leak is located on a pressurised pipe, concealed or buried, and the limits of the method.',
    h1: 'Acoustic leak detection',
    image: acoustiqueImg,
    imageAlt: 'Illustration: electro-acoustic listening on a buried pipe with a ground microphone',
    intro: [
      'A leaking pressurised pipe makes a noise: water escaping through a crack hisses or rushes, and the sound travels along the pipe and through the ground. Electro-acoustic listening amplifies these sounds to find the point where they are loudest, which is directly above the leak.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'A highly sensitive ground microphone is placed point by point along the route of the pipe. The device amplifies and filters the signal to separate the leak noise from background noise. The sound gets louder as you get closer to the leak.',
          'On longer pipes, acoustic correlation uses two sensors on either side of the suspect area. The difference in the time the sound takes to reach each sensor is used to calculate the position of the leak.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          'pressurised mains water pipes, cold water included;',
          '[buried pipes](/en/water-pipe-leak) under a garden or a drive;',
          'pipes under a concrete slab.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'The method needs pressure: it does not work on [drains](/en/drain-leak). Very small leaks make little noise, and background noise (traffic, pumps, appliances running) interferes with listening. Soft ground or loose soil dampens the sound. In those situations, [tracer gas](/en/leak-detection-methods/tracer-gas) takes over.',
        ],
      },
    ],
    related: ['/en/water-pipe-leak', '/en/leak-detection-methods/tracer-gas', '/en/high-water-usage'],
  }),
  technique({
    path: '/en/leak-detection-methods/tracer-gas',
    translationOf: '/techniques/gaz-traceur',
    nav: 'Tracer gas',
    summary: 'A nitrogen-hydrogen mix that rises to the surface directly above even the smallest leaks.',
    serviceType: 'Tracer gas leak detection',
    title: 'Tracer gas leak detection (nitrogen-hydrogen) | Expomis',
    description: 'Nitrogen-hydrogen tracer gas locates small leaks under a screed or underground. How it works, safety, typical uses and limits.',
    h1: 'Tracer gas leak detection',
    image: gazImg,
    imageAlt: 'Hydrogen detector and ground probe on a tiled floor during a tracer gas leak search',
    intro: [
      'Tracer gas is a mix of nitrogen and hydrogen that is non-toxic and non-flammable in this proportion. Injected into a pipe, it escapes through the leak, passes through the screed, tiles or soil, and a highly sensitive detector picks it up at the surface, directly above the leak.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'The water is turned off and the section concerned is drained, then the gas is injected under pressure. Hydrogen is the smallest and lightest molecule: it passes through building materials and rises to the surface. We then sweep the route of the pipe with a probe; the highest concentration marks the leak.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          'small, silent leaks that acoustic detection cannot pick up;',
          '[pipes under a slab or underground](/en/water-pipe-leak);',
          '[underfloor heating](/en/heating-leak) when the thermal camera does not give enough contrast.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'The section being tested must be isolated and drained. Some very tight floor coverings slow the gas down, and draughts disperse it. The method cannot be used on drains, which are not gas-tight by design: we use a [smoke test](/en/leak-detection-methods/smoke-test) instead.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is tracer gas dangerous?',
        a: 'No. The mix we use contains 95% nitrogen and 5% hydrogen: it is non-flammable, non-toxic, odourless and disperses in the air.',
      },
    ],
    related: ['/en/water-pipe-leak', '/en/leak-detection-methods/acoustic-detection', '/en/leak-detection-methods/pressure-test'],
  }),
  technique({
    path: '/en/leak-detection-methods/fluorescein',
    translationOf: '/techniques/fluoresceine',
    nav: 'Fluorescein dye test',
    summary: 'A fluorescent dye that shows where the water is coming from: drain, shower, terrace.',
    serviceType: 'Fluorescein dye testing',
    title: 'Fluorescein dye test for leaks | Expomis',
    description: 'A fluorescein dye test identifies the source of a drain or shower leak, or of water coming in through a terrace. How it works, typical uses and limits.',
    h1: 'Fluorescein dye test for leaks',
    image: fluoresceineImg,
    imageAlt: 'Fluorescein under UV light: dyed water escaping from a trap fitting and running across the tiles',
    intro: [
      'Fluorescein is a non-toxic, fluorescent green dye that is visible at very low concentrations, especially under a UV lamp. We dye the water from one appliance or one specific area, then watch where it reappears: if the dyed water shows up in the damp patch, we know where the leak is coming from.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'The test is done one source at a time: the shower drain, the basin, the WC, a terrace outlet, an upstand in the waterproofing. The dyed water is run in one place only, and the damaged area is watched. When the colour appears, the source has been found.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          '[drains](/en/drain-leak): which appliance or which joint is at fault;',
          '[showers and baths](/en/shower-leak): the waste or the waterproofing;',
          '[water ingress](/en/water-ingress) through a terrace, a balcony or a flat roof, by targeted flood testing;',
          'checking a connection: is an appliance actually connected to the sewer?',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'Fluorescein identifies the source, not the exact point of the leak along the way. It also requires the water to reappear visibly: if it soaks away into the ground, nothing can be seen, and we move on to a [CCTV drain survey](/en/leak-detection-methods/cctv-drain-survey) or a [smoke test](/en/leak-detection-methods/smoke-test).',
        ],
      },
    ],
    faq: [
      {
        q: 'Is fluorescein harmful?',
        a: 'No. It is used at very low concentrations and is also used to trace groundwater, precisely because it is safe for the environment at these doses.',
      },
    ],
    related: ['/en/shower-leak', '/en/drain-leak', '/en/water-ingress'],
  }),
  technique({
    path: '/en/leak-detection-methods/pressure-test',
    translationOf: '/techniques/mise-en-pression',
    nav: 'Pressure test',
    summary: 'Confirms the leak and identifies the circuit involved, before any location work.',
    serviceType: 'Pipe pressure testing',
    title: 'Pressure testing: confirming and isolating a leak | Expomis',
    description: 'A pressure test confirms a leak and identifies the circuit involved (cold water, hot water, heating, underfloor heating loop). How it works and its limits.',
    h1: 'Pressure testing: confirming and isolating a leak',
    image: pressionImg,
    imageAlt: 'Illustration: pressure testing a pipe with a gauge',
    intro: [
      'A pressure test is often the first step in a leak search. A circuit is isolated, pressurised and watched on the gauge: if the pressure drops, the circuit is leaking. It is simple but essential: it confirms that there really is a leak, and tells us which circuit to look on.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'Valves are closed to isolate each circuit: cold water, hot water, secondary return, heating, and each loop of an underfloor heating system from the manifold. Each one is brought to a test pressure and watched for a set time. The circuit that loses pressure is the one that is leaking.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          '[high water consumption](/en/high-water-usage) with no visible signs;',
          '[a boiler that keeps losing pressure](/en/heating-leak);',
          'before locating a leak with thermal imaging or tracer gas, to target the right circuit;',
          'after a repair, to check that the circuit is watertight again.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'A pressure test tells you whether a circuit is leaking, not exactly where. It does not apply to drains, which are not under pressure. A valve that no longer closes properly can skew the result: we take this into account when interpreting it.',
        ],
      },
    ],
    related: ['/en/leak-detection-methods/tracer-gas', '/en/leak-detection-methods/acoustic-detection', '/en/high-water-usage'],
  }),
  technique({
    path: '/en/leak-detection-methods/moisture-mapping',
    translationOf: '/techniques/humidimetre',
    nav: 'Moisture mapping',
    summary: 'Mapping the damp area: how far it extends, and which side the water is coming from.',
    serviceType: 'Moisture measurement of building materials',
    title: 'Moisture mapping: measuring a damp area | Expomis',
    description: 'A moisture meter measures the moisture in building materials to define the affected area, tell a leak from water ingress or rising damp, and monitor drying.',
    h1: 'Moisture mapping: measuring a damp area',
    image: humidimetreImg,
    imageAlt: 'Moisture meter held against a damp-stained wall with flaking paint',
    intro: [
      'A moisture meter measures the moisture content of a material: plaster, concrete, screed, wood, masonry. By taking readings at many points, we build a map of the damp area: how far it extends and, above all, its gradient, which shows which side the water is coming from.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'Depending on the material, readings are taken on the surface without damaging it, or with pins for wood and for readings below the surface. They are taken on a regular grid. The highest values are found closest to the source of the water.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          'at the start of every job, to define the affected area;',
          'to tell a leak apart from [water ingress](/en/water-ingress), condensation or rising damp;',
          'to document the damage in a [water damage claim](/en/water-damage);',
          'to monitor drying after a repair, before the finishes are redone.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'A moisture meter shows where the water is, but not for certain where it comes from: it guides the search, and other methods confirm it. Readings depend on the material, so they are compared with each other on the same surface rather than read as absolute values.',
        ],
      },
    ],
    related: ['/en/water-ingress', '/en/water-damage', '/en/leak-detection-methods/pressure-test'],
  }),
  technique({
    path: '/en/leak-detection-methods/smoke-test',
    translationOf: '/techniques/test-fumigene',
    nav: 'Smoke test',
    summary: 'Non-toxic smoke that escapes through defects in a drainage system and tracks down smells.',
    serviceType: 'Drain smoke testing',
    title: 'Drain smoke testing: leaks and sewer smells | Expomis',
    description: 'A smoke test reveals leaking joints, cracks and defects in a drain or sewer, and the source of bad smells. How it works and its limits.',
    h1: 'Smoke testing: finding defects in drains',
    intro: [
      'A smoke test means sending non-toxic smoke into a drain or sewer system. The smoke follows the pipework and comes out wherever there is a defect: a badly fitted joint, a cracked pipe, a missing trap, a leaking connection. Wherever the smoke appears, water is escaping or smells are getting in.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'A generator blows the smoke into the system through a manhole or an access point, at a slight positive pressure. We then go round the building: rooms, service ducts, cellars, the foot of downpipes, outside areas. Every place the smoke comes out marks a leak. A dry or missing trap also lets smoke through, which often explains sewer smells with no water leak.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          'sewer smells in a home or a cellar;',
          '[leaks on drains](/en/drain-leak) and downpipes;',
          'checking connections and drain ventilation.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'The system has to be accessible through a manhole, a cover or an access point. If the smoke comes out somewhere that cannot be seen, under a slab for example, we follow up with a [CCTV drain survey](/en/leak-detection-methods/cctv-drain-survey).',
        ],
      },
    ],
    faq: [
      {
        q: 'Is the smoke dangerous?',
        a: 'No. It is non-toxic and clears quickly; you just need to air the room after the test.',
      },
    ],
    related: ['/en/drain-leak', '/en/leak-detection-methods/cctv-drain-survey', '/en/leak-detection-methods/fluorescein'],
  }),
  technique({
    path: '/en/leak-detection-methods/cctv-drain-survey',
    translationOf: '/techniques/camera-canalisation',
    nav: 'CCTV drain survey',
    summary: 'A camera pushed into the drains: cracks, displaced joints, roots, back-falls.',
    serviceType: 'CCTV drain survey',
    title: 'CCTV drain and sewer survey | Expomis',
    description: 'A CCTV survey films the inside of drains and sewers: cracks, displaced joints, roots, collapses, back-falls. How it works and its limits.',
    h1: 'CCTV drain and sewer survey',
    image: inspectionImg,
    imageAlt: 'Drain camera screen showing a crack in a pipe, with the cable lowered into a manhole',
    intro: [
      'A waterproof camera on the end of a push rod is fed into the drainage system and films the inside of the pipes. You see the condition of the pipe directly: cracks, displaced joints, roots, collapses, deposits, back-falls. The length of rod paid out tells you how far the defect is from the access point.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'The camera goes in through a manhole, a cover or a rodding eye, then is pushed gradually along the pipe. The picture is shown live on a screen, and the rod counter shows how far each defect is from the entry point. That tells us which section is affected and where to dig, instead of exposing the whole pipe.',
          'Unlike fluorescein or smoke, which show that a defect exists, the camera shows what it is: a crack, a displaced joint or a root do not call for the same repair.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          '[buried drains or drains under a slab](/en/drain-leak);',
          "the house's connection to the sewer;",
          'after a smoke or fluorescein test, to see the defect and decide on the repair;',
          'slow drainage or repeated backing up.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'It needs an access point: a manhole, a cover, a rodding eye. Small diameters and tight bends limit how far the camera can go, and a blocked pipe has to be cleared first.',
        ],
      },
    ],
    related: ['/en/drain-leak', '/en/leak-detection-methods/smoke-test', '/en/leak-detection-methods/endoscopy'],
  }),
  technique({
    path: '/en/leak-detection-methods/endoscopy',
    translationOf: '/techniques/endoscopie',
    nav: 'Endoscopy',
    summary: 'Looking inside a suspended ceiling, a service duct or dry lining without taking anything apart.',
    serviceType: 'Endoscopic inspection',
    title: 'Endoscopy: inspecting inaccessible spaces | Expomis',
    description: 'An endoscope lets you see inside a suspended ceiling, a service duct or under a bath without taking anything apart. Uses and limits in leak detection.',
    h1: 'Endoscopy: inspecting inaccessible spaces',
    intro: [
      'An endoscope is a small camera on the end of a flexible probe. It goes in through an existing opening (access hatch, air vent, joint, fixing hole) or a small drilled hole, to see inside a suspended ceiling, a service duct, dry lining or under a bath, without taking anything apart.',
    ],
    sections: [
      {
        h2: 'How it works',
        body: [
          'The probe is guided into the cavity while the picture is shown on a screen. This lets us follow a pipe, spot a dripping joint, water trails or wet material, and photograph what we see for the report. When a small hole is needed, it is easily filled, nothing like removing a bath panel or a ceiling.',
        ],
      },
      {
        h2: 'When we use it',
        list: [
          'checking a hidden joint behind a bath panel or a concealed WC frame;',
          'inspecting a service duct in a block of flats;',
          'following the path of the water in a suspended ceiling or a cavity;',
          'visually confirming a leak located by another method.',
        ],
      },
      {
        h2: 'Limits',
        body: [
          'An endoscope only sees what is inside a cavity: it is no use for a pipe embedded in a solid screed. Its reach is limited to a few metres from the entry point.',
        ],
      },
    ],
    related: ['/en/water-ingress', '/en/shower-leak', '/en/leak-detection-methods/cctv-drain-survey'],
  }),
];
